/* Build debrief — the dev-facing feedback form that feeds the Admin desk.
   Opens after Approve & deliver on the Review screen. Captures an accuracy
   rating (mapped to pass rate), a ship-confidence call, per-stage signal,
   time saved, a comment, and an optional flagged issue that links straight
   into the ticket system. Writes through window.MAV.recordFeedback / flagIssue. */

const { useState: useFbState } = React;

const SHIP_OPTS = [
  { id: 'ship',  label: 'Ship as-is', icon: (s) => <I.check size={14} /> },
  { id: 'minor', label: 'Minor edits', icon: (s) => <I.retry size={14} /> },
  { id: 'no',    label: 'Not yet',     icon: (s) => <I.x size={14} /> },
];
const SEV_OPTS = ['low', 'medium', 'high', 'blocker'];
const SEV_LBL = { low: 'Low', medium: 'Medium', high: 'High', blocker: 'Blocker' };
const starPct = (n) => [0, 45, 64, 80, 92, 100][n] || 0;

function StarRate({ value, onChange }) {
  const [hover, setHover] = useFbState(0);
  const shown = hover || value;
  const words = ['Rate accuracy', 'Way off', 'Rough', 'Usable', 'Faithful', 'Pixel-perfect'];
  return (
    <div className="fb-rate">
      <div className="fb-stars" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map(i => (
          <button key={i} type="button" className="fb-star" onMouseEnter={() => setHover(i)} onClick={() => onChange(i)} aria-label={i + ' stars'}>
            <I.star size={32} fill={shown >= i ? 'var(--brand-amber)' : undefined} className={shown >= i ? '' : 'empty'} />
          </button>
        ))}
      </div>
      <span className="fb-rate-word">{words[shown]}</span>
    </div>
  );
}

function Segmented({ value, onChange }) {
  return (
    <div className="fb-seg">
      {SHIP_OPTS.map(o => (
        <button key={o.id} type="button" className={'fb-seg-btn' + (value === o.id ? ' active a-' + o.id : '')} onClick={() => onChange(o.id)}>
          {o.icon()}{o.label}
        </button>
      ))}
    </div>
  );
}

function StageSignal({ votes, onVote }) {
  return (
    <div className="fb-stages">
      {window.MAV.stageDefs.map(s => {
        const v = votes[s.id];
        return (
          <div key={s.id} className="fb-stage">
            <span className="fb-stage-no">{s.no}</span>
            <span className="fb-stage-nm">{s.name}</span>
            <div className="fb-stage-btns">
              <button type="button" className={'fb-vote up' + (v === 'up' ? ' on' : '')} onClick={() => onVote(s.id, v === 'up' ? null : 'up')} title="Helped"><I.check size={13} /></button>
              <button type="button" className={'fb-vote down' + (v === 'down' ? ' on' : '')} onClick={() => onVote(s.id, v === 'down' ? null : 'down')} title="Needed cleanup"><I.warn size={12} /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BuildFeedback({ job, onClose, go }) {
  const [rating, setRating] = useFbState(0);
  const [wouldShip, setWouldShip] = useFbState('ship');
  const [stageVotes, setStageVotes] = useFbState({});
  const [time, setTime] = useFbState(2);
  const [comment, setComment] = useFbState('');
  const [issueOn, setIssueOn] = useFbState(false);
  const [issueSev, setIssueSev] = useFbState('medium');
  const [issueTitle, setIssueTitle] = useFbState('');
  const [issueText, setIssueText] = useFbState('');
  const [result, setResult] = useFbState(null);

  const pass = window.MAV.passRate;
  const acc = starPct(rating);
  const vote = (id, v) => setStageVotes(s => ({ ...s, [id]: v }));

  const submit = async () => {
    const flag = (issueOn && issueTitle.trim()) ? { sev: issueSev, title: issueTitle.trim(), text: issueText.trim() } : null;
    // Live backend → a persisted ratings row (subject = the build's owner, not 'jd') + optional flagged issue.
    if (window.GooseAPI && window.GooseAPI.submitFeedback) {
      try {
        const r = await window.GooseAPI.submitFeedback({
          build: job.name, buildId: job._id || job.id, rating: rating || undefined,
          comment: comment.trim(), timeSaved: time, wouldShip, stageVotes, flag,
        });
        if (r && r.flagged && Array.isArray(window.MAV.issues)) window.MAV.issues.unshift(r.flagged);
        setResult({ flagged: (r && r.flagged) || null });
        return;
      } catch (e) { /* network/error → mock fallback below */ }
    }
    const meExt = (window.MAV.currentUser && window.MAV.currentUser.ext_id) || window.MAV.currentUser || 'jd';
    window.MAV.recordFeedback(meExt, { rating: rating || undefined, comment: comment.trim(), build: job.name, timeSaved: time, wouldShip, stageVotes });
    let flagged = null;
    if (flag) flagged = window.MAV.flagIssue({ ...flag, build: job.name, dev: meExt });
    setResult({ flagged });
  };

  /* ---- success state ---- */
  if (result) {
    return ReactDOM.createPortal(
      <div className="fb-overlay" onClick={onClose}>
        <div className="fb-modal fb-done" onClick={e => e.stopPropagation()}>
          <div className="fb-done-ico"><I.check size={30} /></div>
          <h2 className="fb-done-h">Debrief logged</h2>
          <p className="fb-done-p">Your rating and signal are in the Admin desk. {result.flagged ? <>Issue <b>{result.flagged.id}</b> was filed and is waiting in the queue.</> : 'Thanks for the readout, Maverick.'}</p>
          <div className="fb-done-acts">
            <button className="btn btn-ghost" onClick={() => { onClose(); go && go('home'); }}>Back to dashboard</button>
            <button className="btn btn-primary" onClick={() => { onClose(); go && go('job'); }}>View delivered build <I.arrow size={15} /></button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal" onClick={e => e.stopPropagation()}>
        {/* header */}
        <div className="fb-head">
          <div>
            <div className="row gap-8" style={{ marginBottom: 7 }}>
              <span className="chip" style={{ background: 'var(--brand-yellow)', color: 'var(--ink-on-brand)', fontWeight: 700 }}>Debrief</span>
              <span className="chip mono">{job.id}</span>
            </div>
            <h2 className="fb-title">How did your wingman fly?</h2>
            <p className="fb-sub">{job.name}</p>
          </div>
          <button className="icon-act" onClick={onClose} title="Close"><I.x size={16} /></button>
        </div>

        <div className="fb-body">
          {/* accuracy → pass rate */}
          <div className="fb-section">
            <div className="fb-section-h"><span className="fb-q">Accuracy vs the source</span><span className="muted" style={{ fontSize: 11.5 }}>QA pixel-match 96.4% · CONDITIONAL</span></div>
            <StarRate value={rating} onChange={setRating} />
            {rating > 0 && (
              <div className="fb-map">
                <div className="fb-map-row"><span className="fb-map-k">Your accuracy</span><div className="fb-map-bar"><span style={{ width: acc + '%', background: 'var(--grad-warm)' }}></span></div><span className="fb-map-v">{acc}%</span></div>
                <div className="fb-map-row"><span className="fb-map-k">Workspace pass rate</span><div className="fb-map-bar"><span style={{ width: pass + '%', background: 'var(--pass)' }}></span></div><span className="fb-map-v">{pass}%</span></div>
                <p className="fb-map-note">{acc >= pass ? 'You rated this above the QA pass rate — high confidence.' : 'You rated below the pass rate — the gap helps Admin spot where QA over-passes.'}</p>
              </div>
            )}
          </div>

          {/* ship confidence */}
          <div className="fb-section">
            <span className="fb-q">Would you ship it as-is?</span>
            <Segmented value={wouldShip} onChange={setWouldShip} />
          </div>

          {/* stage signal */}
          <div className="fb-section">
            <div className="fb-section-h"><span className="fb-q">Which stages earned their fuel?</span><span className="muted" style={{ fontSize: 11.5 }}>optional · tap to flag</span></div>
            <StageSignal votes={stageVotes} onVote={vote} />
          </div>

          {/* time saved */}
          <div className="fb-section">
            <div className="fb-section-h"><span className="fb-q">Time saved vs hand-coding</span><span className="fb-time-v"><I.clock size={13} /> {time === 0 ? 'None' : '≈ ' + time + ' hr' + (time === 1 ? '' : 's')}</span></div>
            <input className="fb-slider" type="range" min="0" max="6" step="0.5" value={time} onChange={e => setTime(parseFloat(e.target.value))} style={{ '--pct': (time / 6 * 100) + '%' }} />
          </div>

          {/* comment */}
          <div className="fb-section">
            <span className="fb-q">Anything for the record?</span>
            <textarea className="input" rows={2} placeholder="One line the team should see — the good and the bad…" value={comment} onChange={e => setComment(e.target.value)} style={{ resize: 'none' }} />
          </div>

          {/* flag an issue */}
          <div className={'fb-flag' + (issueOn ? ' open' : '')}>
            <button type="button" className="fb-flag-toggle" onClick={() => setIssueOn(o => !o)}>
              <span className="row gap-8"><I.flag size={15} style={{ color: 'var(--fail)' }} /> Flag an issue with this build</span>
              <I.chevron size={15} className="muted" style={{ transform: issueOn ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
            </button>
            {issueOn && (
              <div className="fb-flag-body">
                <div className="fb-sev">
                  {SEV_OPTS.map(s => (
                    <button key={s} type="button" className={'fb-sev-btn sev-' + s + (issueSev === s ? ' on' : '')} onClick={() => setIssueSev(s)}>{SEV_LBL[s]}</button>
                  ))}
                </div>
                <input className="input" placeholder="Short title — e.g. Hero crop softens above 600px" value={issueTitle} onChange={e => setIssueTitle(e.target.value)} style={{ margin: '10px 0' }} />
                <textarea className="input" rows={2} placeholder="What happened, and how to reproduce it…" value={issueText} onChange={e => setIssueText(e.target.value)} style={{ resize: 'none' }} />
                <p className="fb-flag-note"><I.link size={11} /> Files a ticket in Admin › Issues, linked to <b>{job.name}</b> — assignable, with its own thread.</p>
              </div>
            )}
          </div>
        </div>

        {/* footer */}
        <div className="fb-foot">
          <span className="muted" style={{ fontSize: 11.5 }}>{issueOn && issueTitle.trim() ? 'Will submit a rating + 1 flagged issue' : 'Submits to the Admin desk'}</span>
          <div className="row gap-10">
            <button className="btn btn-quiet" onClick={() => { onClose(); go && go('job'); }}>Skip</button>
            <button className="btn btn-primary btn-lg" onClick={submit} disabled={!rating} title={rating ? 'Send debrief' : 'Add an accuracy rating first'}><I.send size={15} /> Submit debrief</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

Object.assign(window, { BuildFeedback });
